/**
 * FreshCards — Host Adapter Example (FreshCards-side reference impl)
 *
 * Phase 4 reference implementation of a HOST that mounts FreshCards via
 * EmbeddingAPIv2. This shows how a real host (e.g. Vibecoder, Gallery,
 * HQ) wires the FreshCards runtime into its own UI.
 *
 * The canonical pattern:
 *   1. Host implements HostRenderer { mount, unmount }
 *   2. Host implements hostCallbacks: onNavigate, onSurfaceChange,
 *      onCommandExecute (see ../embedding/hostCallbacks.ts)
 *   3. Host builds an EmbeddingBundleV2 (theme + layout + adapters)
 *   4. Host calls mountRuntime(bundle, renderer) — FreshCards mounts
 *      into the host's DOM container
 *   5. Host exposes globalThis.__fvs_host so any other code in the
 *      host can find the adapter and call back into FreshCards
 *
 * This file is exported from src/lib.ts so it ships with the library
 * bundle (dist/freshcards.es.js). Real hosts can copy/adapt the
 * reference or import it as a starting point.
 *
 * IMPORTANT: This is an example for FreshCards-as-library consumption.
 * Real host apps (Vibecoder, Gallery, HQ) will have their own host
 * adapters tailored to their UX — this file is the canonical pattern
 * they should follow.
 */

import type { EmbeddingBundleV2, HostRenderer } from './EmbeddingAPIv2';
import { mountRuntime } from './EmbeddingAPIv2';
import type { FreshCardsHostCallbacks, HostCallbacks, HostNavigatePayload, HostSurfaceChangePayload, HostCommandPayload } from './hostCallbacks';
import { noopHostCallbacks } from './hostCallbacks';

/**
 * The FreshCards-side host adapter. A real host would instantiate this
 * (or its own equivalent) and pass it as the HostRenderer to mountRuntime.
 */
export class FreshCardsHostAdapter implements HostRenderer {
  /** Global handle so any code in the host can find the adapter. */
  static GLOBAL_KEY = '__fvs_host' as const;

  private mountedContainers = new Set<HTMLElement>();
  private callbacks: Required<FreshCardsHostCallbacks>;

  constructor(callbacks: Partial<FreshCardsHostCallbacks> = {}) {
    this.callbacks = {
      ...noopHostCallbacks,
      ...callbacks,
    } as Required<FreshCardsHostCallbacks>;
  }

  /**
   * Mount FreshCards into the given container.
   * Called by EmbeddingAPIv2.mountRuntime after the bundle's container
   * selector resolves to this element.
   */
  mount(container: HTMLElement, bundle: EmbeddingBundleV2): void {
    if (this.mountedContainers.has(container)) {
      // Already mounted — unmount first to avoid duplicates.
      this.unmount(container);
    }

    // Expose self as globalThis.__fvs_host so any code in the host
    // (including FreshCards internals) can reach the adapter.
    (globalThis as Record<string, unknown>)[FreshCardsHostAdapter.GLOBAL_KEY] = this;

    // Set up the container — clear it, add a marker, host the FreshCards
    // runtime shell. The actual FreshCards content is mounted by the
    // host's renderer logic (typically React.createRoot or similar).
    container.dataset.fcHostMounted = '1';
    container.dataset.fcBundleId = bundle.id;

    this.mountedContainers.add(container);
  }

  /**
   * Unmount FreshCards from the given container.
   * Reverses everything `mount` did.
   */
  unmount(container: HTMLElement): void {
    if (!this.mountedContainers.has(container)) return;

    delete container.dataset.fcHostMounted;
    delete container.dataset.fcBundleId;
    // Clear the container's contents (defensive — host's renderer
    // typically does this via its own unmount logic).
    while (container.firstChild) container.removeChild(container.firstChild);

    this.mountedContainers.delete(container);

    if (this.mountedContainers.size === 0) {
      delete (globalThis as Record<string, unknown>)[FreshCardsHostAdapter.GLOBAL_KEY];
    }
  }

  /**
   * Convenience: build a bundle, mount it, return ok/error.
   * Hosts typically call this from their boot sequence.
   */
  mountBundle(bundle: EmbeddingBundleV2): { ok: boolean; error?: string } {
    return mountRuntime(bundle, this);
  }

  // ─── Host callbacks (3 required + extras via spread) ──────────────────────

  /**
   * Host callback: FreshCards wants to navigate to a URL inside the host.
   * The host decides how to handle (route via React Router, push history,
   * open a modal, etc.). Default is no-op.
   */
  onNavigate(payload: HostNavigatePayload): void {
    this.callbacks.onNavigate?.(payload);
  }

  /**
   * Host callback: FreshCards switched to a new surface (page, database,
   * editor, etc.). Host can update breadcrumbs, deep-link state, etc.
   */
  onSurfaceChange(payload: HostSurfaceChangePayload): void {
    this.callbacks.onSurfaceChange?.(payload);
  }

  /**
   * Host callback: FreshCards fired a command (e.g. "open settings",
   * "export to PDF", "share link"). Host decides how to handle.
   */
  onCommandExecute(payload: HostCommandPayload): void {
    this.callbacks.onCommandExecute?.(payload);
  }

  /** Get the global adapter instance if one is registered. */
  static getGlobal(): FreshCardsHostAdapter | null {
    return ((globalThis as Record<string, unknown>)[FreshCardsHostAdapter.GLOBAL_KEY] as FreshCardsHostAdapter | undefined) ?? null;
  }
}

export type { FreshCardsHostCallbacks, HostCallbacks };
